import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import DropDownInput from '../../../../../../../../../../../../components/forms/dropdown-input/DropDownInput';
import NumberInput from '../../../../../../../../../../../../components/forms/number-input/NumberInput';
import TextAreaInput from '../../../../../../../../../../../../components/forms/text-area-input/TextAreaInput';
import TextInplaceEdit from '../../../../../../../../../../../../components/forms/text-inplace-edit/TextInplaceEdit';
import TextInput from '../../../../../../../../../../../../components/forms/text-input/TextInput';
import { EMISSION_MAX_FRACTION_DIGIT } from '../../../../../../../../../../../../shared/constants/products.constant';
import { IMachine, PROCESS_DROPDOWN_OPTIONS } from '../../../../PcfWizard.type';
import RemoveProcessButton from '../../RemoveProcessButton';
import InputMaterial from '../input-material/InputMaterial';
import { getEnergySourceOptions } from './helper/getEnergySourceOptions';

interface IndividualProcessFormProps {
  name: string;
  processIndex: number;
  onDelete: (showConfirmationDialog: boolean) => void;
}

const IndividualProcessForm = ({ name, processIndex, onDelete }: IndividualProcessFormProps) => {
  const { t } = useTranslation();
  const { control, setValue } = useFormContext();
  const processes = useWatch({
    name: 'processes',
    control,
  });
  const machines = useWatch({
    name: `processes[${processIndex}].machines`,
    control,
  });

  const onRemoveInputMaterialCard = () => {
    setValue(`processes[${processIndex}].input_materials`, undefined);
  };

  return (
    <div className='mb-3'>
      <div className='flex flex-row w-full justify-content-between align-items-center'>
        <div className='w-full'>
          <TextInplaceEdit
            name={`${name}[${processIndex}].name`}
            placeholder={t(
              'footprintDetailsPage.sections.calculatePcfSection.individualProcessSection.inputs.placeholders.processName',
            )}
            value={processes[processIndex].name}
            control={control}
            textSize='text-2xl'
          />
        </div>
        <RemoveProcessButton
          onClick={() => {
            return onDelete(true);
          }}
        />
      </div>
      <div className='mt-4 mb-4 font-light text-black-alpha-60'>
        {t(
          'footprintDetailsPage.sections.calculatePcfSection.individualProcessSection.description',
        )}
      </div>
      {/* Input Material Section
        - Currently only one input material is allowed but in future we might allow multiple
      */}
      {processes[processIndex].input_materials && (
        <InputMaterial
          formPath={`${name}[${processIndex}].input_materials[0]`}
          processIndex={processIndex}
          processType={PROCESS_DROPDOWN_OPTIONS.INDIVIDUAL_PROCESS}
          onRemoveInputMaterialCard={onRemoveInputMaterialCard}
        />
      )}
      <div className='mt-4'>
        {/* Machine Section */}
        {machines?.map((machine: IMachine, index: number) => {
          return (
            <div key={index} className='surface-ground border-round-2xl p-3'>
              <TextInplaceEdit
                name={`${name}[${processIndex}].machines[${index}].name`}
                placeholder={t(
                  'footprintDetailsPage.sections.calculatePcfSection.individualProcessSection.inputs.placeholders.machineName',
                )}
                value={machine.name}
                control={control}
                textSize='text-lg'
              />
              <div className='mt-4 mb-4 font-light text-black-alpha-60'>
                {t(
                  'footprintDetailsPage.sections.calculatePcfSection.individualProcessSection.machine_description',
                )}
              </div>
              <div className='grid'>
                <div className='col-6'>
                  <NumberInput
                    name={`processes[${processIndex}].machines[${index}].amount`}
                    control={control}
                    label={t(
                      'footprintDetailsPage.sections.calculatePcfSection.individualProcessSection.inputs.placeholders.energyPerPiece',
                    )}
                    minFractionDigits={0}
                    maxFractionDigits={EMISSION_MAX_FRACTION_DIGIT}
                  />
                </div>
                <div className='col-6'>
                  <DropDownInput
                    name={`processes[${processIndex}].machines[${index}].energy_source`}
                    control={control}
                    label={t(
                      'footprintDetailsPage.sections.calculatePcfSection.individualProcessSection.inputs.placeholders.energySource',
                    )}
                    options={getEnergySourceOptions(t) || []}
                  />
                </div>
                {/* <div className='col-6'>
                  <Button
                    label={t(
                      'footprintDetailsPage.sections.calculatePcfSection.individualProcessSection.machineSection.assessDataQuality',
                    )}
                    className='p-button-primary mb-2'
                  />
                </div> */}
                <div className='col-12'>
                  <TextAreaInput
                    name={`processes[${processIndex}].machines[${index}].comment`}
                    label={t(
                      'footprintDetailsPage.sections.calculatePcfSection.individualProcessSection.inputs.placeholders.comment',
                    )}
                    control={control}
                    rows={3}
                    className='w-full h-full'
                  />
                </div>

                {/* TODO: Remove this when implementing calculation with individual process */}
                <div>
                  <TextInput
                    name={`processes[${processIndex}].machines[${index}].quality_assessment`}
                    control={control}
                    value={'TODO'}
                    defaultValue={'TODO'}
                    type='hidden'
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default IndividualProcessForm;
